import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Anchor, Button, PasswordInput, Stack, Text, TextInput, Title, Progress } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import * as authApi from '@/features/auth/api';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { ensureFingerprint } from '@/shared/lib/fingerprint';
import { env } from '@/shared/lib/env';
import { passwordSchema, computeStrength } from '@/features/auth/password-utils';

const registerSchema = z.object({
  firstName: z.string().min(2, 'Min 2 caratteri'),
  lastName: z.string().min(2, 'Min 2 caratteri'),
  email: z.string().email('Email non valida'),
  password: passwordSchema,
});

type RegisterForm = z.infer<typeof registerSchema>;

export const Route = createFileRoute('/_auth/register')({ component: RegisterPage });

function RegisterPage() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const form = useForm<RegisterForm>({
    mode: 'controlled',
    initialValues: { firstName: '', lastName: '', email: '', password: '' },
    validate: zodResolver(registerSchema),
  });
  const strength = computeStrength(form.values.password);

  const onSubmit = form.onSubmit(async (values) => {
    setBusy(true);
    try {
      const fingerprint = await ensureFingerprint();
      await authApi.register({
        Email: values.email,
        Password: values.password,
        FirstName: values.firstName,
        LastName: values.lastName,
        FingerPrint: fingerprint,
        UserLang: env.VITE_DEFAULT_LANG,
      });
      notifications.show({ color: 'green', message: 'Registrato. Controlla la tua email per il codice OTP.' });
      void navigate({ to: '/register-otp', search: { email: values.email } });
    } catch (err) {
      const msg = err instanceof JsonApiHttpError ? (err.errors[0]?.detail ?? err.errors[0]?.title ?? 'Errore') : err instanceof Error ? err.message : 'Errore';
      notifications.show({ color: 'red', title: 'Registrazione fallita', message: msg });
    } finally {
      setBusy(false);
    }
  });

  return (
    <Stack>
      <div>
        <Title order={2}>Crea il tuo account</Title>
        <Text c="dimmed" size="sm">
          Già registrato? <Anchor component={Link} to="/login">Accedi</Anchor>
        </Text>
      </div>
      <form onSubmit={onSubmit}>
        <Stack>
          <TextInput label="Nome" required {...form.getInputProps('firstName')} autoComplete="given-name" />
          <TextInput label="Cognome" required {...form.getInputProps('lastName')} autoComplete="family-name" />
          <TextInput label="Email" required type="email" autoComplete="email" {...form.getInputProps('email')} />
          <PasswordInput label="Password" required autoComplete="new-password" {...form.getInputProps('password')} />
          {form.values.password.length > 0 && (
            <div>
              <Progress value={(strength.score / 4) * 100} color={strength.color} size="sm" />
              <Text size="xs" c={strength.color} mt={4}>
                Robustezza: {strength.label}
              </Text>
            </div>
          )}
          <Button type="submit" loading={busy} fullWidth size="md">
            Crea account
          </Button>
        </Stack>
      </form>
    </Stack>
  );
}
